// Domaine public : liste des autorisations d'occupation (état, catégorie, en cours) et carte
// territoriale avec les marchés (points) et les autorisations (emprise ou position) servis par
// GET /carte/marches et GET /carte/domaine-public pour l'emprise de l'échelle affichée.
import { CATEGORIES_OCCUPATION, CODES_CATEGORIES_OCCUPATION, ETATS_AUTORISATION } from '@cc/contrats';
import { Bandeau, CarteTerritoire, EtatVide, Filtres, Lien, LienBouton, PuceEtat, Squelette, Tableau, useFiltresPersistes, numeroMarqueur, type ColonneTableau } from '@cc/ui';
import { MapPinned } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAutorisations, useCarteDomainePublic, useCarteMarches } from './api';
import { surfacesAutorisations, useEchellesTerritoire } from './carte';
import { aujourdhuiIso, LIBELLES_ETAT_AUTORISATION, marqueursAutorisations, marqueursMarches, montant } from './logique';
import type { Autorisation } from './types';
import styles from './marches.module.css';
import commun from '../../commun/commun.module.css';

const TON_ETAT: Record<string, 'ok' | 'attente' | 'retard' | 'neutre'> = { active: 'ok', expiree: 'neutre', retiree: 'retard', annulee: 'retard' };
const LIBELLES_TON = { ouvert: 'Marché', retard: 'Autorisation expirant sous 30 jours', autre: 'Autorisation en cours' };

/** Carte des marchés et des autorisations ; la liste voisine porte les mêmes numéros que les marqueurs. */
export function CarteMarchesDomaine({ enCours, actif, onActif }: { enCours: boolean; actif: string | null; onActif: (id: string | null) => void }) {
  const t = useEchellesTerritoire();
  const marches = useCarteMarches(t.bbox);
  const autorisations = useCarteDomainePublic(t.bbox, enCours);
  const aujourdhui = aujourdhuiIso();
  const marqueurs = useMemo(() => {
    const m = marqueursMarches(marches.data);
    return [...m, ...marqueursAutorisations(autorisations.data, m.length, aujourdhui)];
  }, [marches.data, autorisations.data, aujourdhui]);
  const surfaces = useMemo(() => surfacesAutorisations(autorisations.data), [autorisations.data]);
  const erreurCouche = marches.error ?? autorisations.error;
  return (
    <div className={styles.disposition}>
      <div className={styles.carteCadre}>
        {t.territoire.isError ? <Bandeau type="erreur">Le territoire de la collectivité n&apos;a pas pu être chargé.</Bandeau> : null}
        {t.echelles?.mention ? <Bandeau type="info">{t.echelles.mention}</Bandeau> : null}
        {erreurCouche ? <Bandeau type="info">Les couches des marchés ou du domaine public ne sont pas disponibles (droits ou module inactif) : la carte s&apos;affiche sans leurs marqueurs.</Bandeau> : null}
        {t.pret && t.echelles && t.echelle ? (
          <CarteTerritoire
            titre="Marchés et occupations du domaine public"
            echelles={t.echelles.echelles}
            echelle={t.echelle}
            onEchelle={t.setEchelle}
            marqueurs={marqueurs}
            marqueurActif={actif}
            onMarqueur={onActif}
            surfaces={surfaces}
            libellesTon={LIBELLES_TON}
            libelleObjets={{ singulier: 'objet localisé', pluriel: 'objets localisés' }}
            hauteur="min(60vh, 560px)"
          />
        ) : (
          <Squelette hauteurBloc={400} libelle="Chargement de la carte" />
        )}
        <p className={styles.aide}>
          Emprise interrogée : {t.bbox ? <span className="cc-mono">{t.bbox}</span> : 'aucune limite connue, aucune couche demandée'}. Les emprises saisies sont dessinées ; une autorisation sans emprise est marquée à la position du dossier.
        </p>
      </div>
      <aside className={styles.cote} aria-label="Objets localisés">
        <ol className={styles.listeCarte} aria-label="Objets localisés">
          {marqueurs.map((m) => (
            <li key={m.id}>
              <button type="button" className={styles.elementCarte} aria-pressed={actif === m.id} onClick={() => onActif(actif === m.id ? null : m.id)}>
                <span className={styles.numero} aria-hidden>
                  {numeroMarqueur(m.numero)}
                </span>
                <span>
                  {m.libelle}
                  <span className={styles.sous}>{LIBELLES_TON[m.ton]}</span>
                </span>
              </button>
              {actif === m.id ? (
                <Lien href={m.ton === 'ouvert' ? `/marches/${m.id}` : `/marches/domaine-public/${m.id}`} className={styles.ouvrir}>
                  Ouvrir la fiche
                </Lien>
              ) : null}
            </li>
          ))}
        </ol>
        {t.bbox && marqueurs.length === 0 && !marches.isPending && !autorisations.isPending ? <p className={styles.aide}>Aucun marché localisé ni autorisation dans cette emprise.</p> : null}
      </aside>
    </div>
  );
}

export function PageDomainePublic() {
  const [filtres, setFiltres] = useFiltresPersistes('domaine-public', { etat: '', categorie: '', en_cours: '' });
  const parametres: Record<string, string> = {};
  if (filtres.etat) parametres.etat = filtres.etat;
  if (filtres.categorie) parametres.categorie = filtres.categorie;
  if (filtres.en_cours) parametres.en_cours = filtres.en_cours;
  const liste = useAutorisations(parametres);
  const [actif, setActif] = useState<string | null>(null);
  const colonnes: ColonneTableau<Autorisation>[] = [
    { id: 'numero', entete: 'Numéro', enTeteLigne: true, masquable: false, valeurTri: (a) => a.numero, cellule: (a) => <Lien href={`/marches/domaine-public/${a.id}`} className={commun.reference}>{a.numero}</Lien> },
    { id: 'categorie', entete: 'Catégorie', cellule: (a) => a.categorie_libelle, valeurTri: (a) => a.categorie },
    { id: 'titulaire', entete: 'Titulaire', cellule: (a) => `${a.titulaire.nom}${a.enseigne ? ` (${a.enseigne})` : ''}` },
    { id: 'adresse', entete: 'Adresse', cellule: (a) => a.adresse ?? 'non renseignée' },
    { id: 'surface', entete: 'Surface', numerique: true, cellule: (a) => `${a.surface_m2.toLocaleString('fr-FR')} m²`, valeurTri: (a) => a.surface_m2 },
    { id: 'periode', entete: 'Période', cellule: (a) => `${date(a.periode.debut)} au ${date(a.periode.fin)}`, valeurTri: (a) => a.periode.fin },
    { id: 'creance', entete: 'Créance', cellule: (a) => (a.creance.statut === 'emise' ? `${a.creance.numero ?? ''} ${montant(a.creance.montant_du)}` : 'en attente du module des recettes') },
    { id: 'etat', entete: 'État', cellule: (a) => <PuceEtat ton={TON_ETAT[a.etat] ?? 'neutre'} libelle={LIBELLES_ETAT_AUTORISATION[a.etat]} />, valeurTri: (a) => a.etat },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Domaine public"
        titre="Occupations du domaine public"
        description="Autorisations nées de la décision favorable d'une demande d'occupation : terrasses, kiosques, installations, manifestations ; emprise sur la carte du territoire."
        actions={
          <LienBouton href="/marches" variante="secondaire">
            Marchés
          </LienBouton>
        }
      />
      <Filtres
        groupes={[
          { id: 'en_cours', libelle: 'Période', options: [{ valeur: '', libelle: 'Toutes' }, { valeur: 'oui', libelle: 'En cours aujourd\'hui' }] },
          { id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Tous' }, ...ETATS_AUTORISATION.map((e) => ({ valeur: e, libelle: LIBELLES_ETAT_AUTORISATION[e] }))] },
          { id: 'categorie', libelle: 'Catégorie', options: [{ valeur: '', libelle: 'Toutes' }, ...CODES_CATEGORIES_OCCUPATION.map((c) => ({ valeur: c, libelle: CATEGORIES_OCCUPATION[c] }))] },
        ]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <CarteMarchesDomaine enCours={filtres.en_cours === 'oui'} actif={actif} onActif={setActif} />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L2-B" fonction="La liste des autorisations">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune autorisation" icone={MapPinned}>
            <p>Aucune autorisation ne correspond à ces filtres. Une autorisation naît de la décision favorable d&apos;un dossier d&apos;occupation du domaine public.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Autorisations" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(a) => a.id} cleColonnes="autorisations" />
        )}
      </EtatRequete>
    </>
  );
}
